import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { ApiError, mediaUrl, submitReview, uploadReviewPhotos } from "@/lib/api";
import { haptic } from "@/lib/telegram";
import { MainAction } from "./MainAction";

export function ReviewSheet({
  product,
  open,
  onClose,
  onSent,
}: {
  product: { id: string; name: string } | null;
  open: boolean;
  onClose: () => void;
  onSent: (productId: string) => void;
}) {
  const { t } = useI18n();
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRating(0);
    setText("");
    setPhotos([]);
    setSent(false);
    setError(null);
  }, [open, product?.id]);

  if (!open || !product) return null;

  const handleSubmit = async () => {
    if (sent) return onClose();
    if (rating === 0) {
      setError(t("reviews.ratingRequired"));
      haptic("error");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitReview({ productId: product.id, rating, text: text.trim(), photos });
      haptic("success");
      setSent(true);
      onSent(product.id);
    } catch (err) {
      haptic("error");
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        {sent ? (
          <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
            <div className="sheet__success-icon">✓</div>
            <h2 className="sheet__title">{t("reviews.sentTitle")}</h2>
            <p className="sheet__text">{t("reviews.sentBody")}</p>
          </div>
        ) : (
          <>
            <h2 className="sheet__title">{t("reviews.sheetTitle")}</h2>
            <p className="sheet__text">
              <strong>{product.name}</strong>
            </p>
            <div className="field" style={{ marginBottom: 6 }}>
              <label>{t("reviews.ratingLabel")}</label>
            </div>
            <div className="stars-input" role="radiogroup" aria-label={t("reviews.ratingLabel")}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n}`}
                  data-active={n <= rating}
                  onClick={() => {
                    haptic("selection");
                    setRating(n);
                  }}
                >
                  ★
                </button>
              ))}
            </div>
            <div className="field">
              <textarea rows={4} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("reviews.textPlaceholder")} />
            </div>
            <div className="review-photos review-photos--edit">
              {photos.map((p) => (
                <span key={p} className="review-photos__item">
                  <img src={mediaUrl(p)} alt="" />
                  <button onClick={() => setPhotos((prev) => prev.filter((x) => x !== p))} aria-label="✕">
                    ✕
                  </button>
                </span>
              ))}
              {photos.length < 3 && (
                <label className="review-photos__add">
                  {uploading ? "…" : `+ ${t("reviews.addPhoto")}`}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    hidden
                    disabled={uploading}
                    onChange={async (e) => {
                      const files: File[] = Array.from((e.target as HTMLInputElement).files ?? []).slice(0, 3 - photos.length);
                      e.target.value = "";
                      if (!files.length) return;
                      setUploading(true);
                      setError(null);
                      try {
                        const urls = await uploadReviewPhotos(files);
                        setPhotos((prev) => [...prev, ...urls].slice(0, 3));
                      } catch (err) {
                        setError(err instanceof ApiError ? err.message : t("common.error"));
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                </label>
              )}
            </div>
            {error && <div className="field__error">{error}</div>}
          </>
        )}
        <MainAction text={sent ? t("inquiry.close") : t("reviews.submit")} onClick={handleSubmit} loading={submitting} hidden={!open} />
      </div>
    </div>
  );
}
